const normalizeSportKey = (sport) => String(sport || "").trim().toLowerCase();

const getAthleteSportKey = (athlete) => normalizeSportKey(
  athlete?._top_play_source_league || athlete?.league || athlete?.sport
);

const isBoardRefreshCurrent = ({
  stopped = false,
  observedRequestToken,
  currentRequestToken,
  requestedSport,
  activeSport,
} = {}) => (
  !stopped &&
  observedRequestToken === currentRequestToken &&
  normalizeSportKey(requestedSport) === normalizeSportKey(activeSport)
);

const filterAthletesForRequestedSport = (athletes, requestedSport, topPlaysSport = "topplays") => {
  const rows = Array.isArray(athletes) ? athletes : [];
  const requestedSportKey = normalizeSportKey(requestedSport);

  if (!requestedSportKey || requestedSportKey === normalizeSportKey(topPlaysSport)) {
    return rows;
  }

  return rows.filter((athlete) => {
    const athleteSportKey = getAthleteSportKey(athlete);
    return !athleteSportKey || athleteSportKey === requestedSportKey;
  });
};

const shouldDeferBoardResumeRefresh = ({
  loading = false,
  initialLoadInFlight = false,
  bootstrapPending = false,
  refreshInFlight = false,
} = {}) => (
  Boolean(loading) ||
  Boolean(initialLoadInFlight) ||
  Boolean(bootstrapPending) ||
  Boolean(refreshInFlight)
);

const buildTopPlaysMetadataRecovery = (
  latestStats,
  cachedSports = [],
  topPlaysSport = "topplays",
  popularStat = "projected_popular"
) => {
  const statsMap = latestStats && typeof latestStats === "object" && !Array.isArray(latestStats)
    ? latestStats
    : {};
  const latestSports = Object.keys(statsMap);
  if (latestSports.length === 0) {
    return null;
  }

  const normalizedTopPlaysSport = normalizeSportKey(topPlaysSport);
  const verifiedStats = {
    ...statsMap,
    [normalizedTopPlaysSport]: statsMap[normalizedTopPlaysSport] || { [popularStat]: "TOP PICKS" },
  };
  const preferredSports = Array.isArray(cachedSports) && cachedSports.length > 0
    ? cachedSports
    : latestSports;
  const sports = [
    normalizedTopPlaysSport,
    ...preferredSports.filter((sportKey, index) => (
      normalizeSportKey(sportKey) !== normalizedTopPlaysSport &&
      Boolean(verifiedStats[sportKey]) &&
      preferredSports.indexOf(sportKey) === index
    )),
  ];

  return {
    verifiedStats,
    sports,
    sport: normalizedTopPlaysSport,
    stats: verifiedStats[normalizedTopPlaysSport],
    stat: popularStat,
  };
};

const settleBoardRequestWithin = (
  request,
  {
    timeoutMs = 0,
    signal = null,
    onTimeout = null,
    onAbort = null,
    timeoutValue = null,
    abortedValue = null,
  } = {}
) => new Promise((resolve, reject) => {
  let settled = false;
  let timeoutId = null;

  const cleanup = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    if (signal && typeof signal.removeEventListener === "function") {
      signal.removeEventListener("abort", handleAbort);
    }
  };
  const finish = (callback, value) => {
    if (settled) {
      return;
    }
    settled = true;
    cleanup();
    callback(value);
  };
  const handleAbort = () => {
    if (typeof onAbort === "function") {
      onAbort();
    }
    finish(resolve, abortedValue);
  };

  if (signal?.aborted) {
    handleAbort();
    return;
  }

  if (signal && typeof signal.addEventListener === "function") {
    signal.addEventListener("abort", handleAbort, { once: true });
  }

  if (Number.isFinite(Number(timeoutMs)) && Number(timeoutMs) > 0) {
    timeoutId = setTimeout(() => {
      if (typeof onTimeout === "function") {
        onTimeout();
      }
      finish(resolve, timeoutValue);
    }, Number(timeoutMs));
  }

  Promise.resolve(request).then(
    (value) => finish(resolve, value),
    (error) => finish(reject, error)
  );
});

module.exports = {
  buildTopPlaysMetadataRecovery,
  filterAthletesForRequestedSport,
  isBoardRefreshCurrent,
  normalizeSportKey,
  settleBoardRequestWithin,
  shouldDeferBoardResumeRefresh,
};
